import type { ReactNode } from 'react';
import {
    AppShell,
    Avatar,
    BlockStack,
    Box,
    CollapsibleSidebar,
    Icon,
    IconButton,
    InlineStack,
    SidebarNavItem,
    SidebarSectionLabel,
    Typography,
    useTheme,
} from '@pv-frontend/ui-components-web';
import logoLight from '../assets/logo-light.png';
import logoDark from '../assets/logo-dark.png';

export interface AdminChromeProps {
    operatorName: string;
    operatorEmail: string;
    onSignOut: () => void;
    /** Current route path, used to highlight the matching nav item (default `/`). */
    activePath?: string;
    children: ReactNode;
}

/**
 * Presentational admin console chrome — brand row, nav rail and operator
 * account row, with no auth dependency so it renders in tests and the
 * /preview.html harness. `AdminShell` binds it to MSAL.
 */
export function AdminChrome({
    operatorName,
    operatorEmail,
    onSignOut,
    activePath = '/',
    children,
}: AdminChromeProps) {
    const { resolvedColorScheme } = useTheme();
    const logo = resolvedColorScheme === 'dark' ? logoLight : logoDark;

    const header = (
        <InlineStack gap="300" blockAlign="center" wrap={false}>
            <img src={logo} alt="" width={32} height={32} style={{ display: 'block' }} />
            <BlockStack gap="0">
                <Typography variant="heading-sm" as="span">
                    PropertyVault
                </Typography>
                <Typography variant="body-sm" tone="text-secondary">
                    Admin console
                </Typography>
            </BlockStack>
        </InlineStack>
    );

    const footer = (
        <InlineStack gap="300" blockAlign="center" wrap={false}>
            <Avatar name={operatorName} size={40} online />
            <Box flex={1} minWidth={0}>
                <BlockStack gap="0">
                    <Typography variant="body-sm" fontWeight="semibold" numberOfLines={1}>
                        {operatorName}
                    </Typography>
                    <Typography variant="body-sm" tone="text-secondary" numberOfLines={1}>
                        {operatorEmail}
                    </Typography>
                </BlockStack>
            </Box>
            <IconButton icon="log-out" accessibilityLabel="Sign out" onPress={onSignOut} />
        </InlineStack>
    );

    return (
        <AppShell
            sidebar={
                <CollapsibleSidebar header={header} footer={footer}>
                    <SidebarSectionLabel>Platform</SidebarSectionLabel>
                    <SidebarNavItem href="/" active={activePath === '/'} icon={<Icon name="flag" />}>
                        Feature flags
                    </SidebarNavItem>
                    <SidebarNavItem
                        href="/tenants"
                        active={activePath.startsWith('/tenants') || activePath.startsWith('/vaults')}
                        icon={<Icon name="home" />}
                    >
                        Tenants
                    </SidebarNavItem>
                    <SidebarNavItem
                        href="/experiments"
                        active={activePath.startsWith('/experiments')}
                        icon={<Icon name="settings" />}
                    >
                        Experiments
                    </SidebarNavItem>
                    <SidebarNavItem
                        href="/ops"
                        active={activePath === '/ops'}
                        icon={<Icon name="activity" />}
                    >
                        Service status
                    </SidebarNavItem>
                    <SidebarNavItem
                        href="/audit"
                        active={activePath.startsWith('/audit')}
                        icon={<Icon name="clock" />}
                    >
                        Audit log
                    </SidebarNavItem>

                    <SidebarSectionLabel>Listings</SidebarSectionLabel>
                    <SidebarNavItem
                        href="/listings/crawls"
                        active={activePath.startsWith('/listings')}
                        icon={<Icon name="search" />}
                    >
                        Crawls
                    </SidebarNavItem>

                    <SidebarSectionLabel>Data</SidebarSectionLabel>
                    <SidebarNavItem
                        href="/data/health"
                        active={activePath.startsWith('/data')}
                        icon={<Icon name="activity" />}
                    >
                        Data health
                    </SidebarNavItem>
                </CollapsibleSidebar>
            }
        >
            <Box minHeight="100%">{children}</Box>
        </AppShell>
    );
}
